import { useState } from "react";
import { router } from "@inertiajs/react";
import type { Category } from "@/constants/forms";

export interface Demographics {
  gender: "any" | "male" | "female";
  minAge: number;
  maxAge: number;
}

export interface FormData {
  imageFile: File | null;
  croppedFile: File | null;
  category: Category | null;
  isPaid: boolean;
  votesTarget: number;
  demographics: Demographics | null;
  photoId: number | null;
}

interface UseSubmissionFormOptions {
  photoId?: number;
}

const createInitialFormData = (options?: UseSubmissionFormOptions): FormData => ({
  imageFile: null,
  croppedFile: null,
  category: null,
  isPaid: false,
  votesTarget: 10,
  demographics: {
    gender: "any",
    minAge: 18,
    maxAge: 65,
  },
  photoId: options?.photoId ?? null,
});

interface UseSubmissionFormReturn {
  formData: FormData;
  isSubmitting: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  setImageFile: (file: File | null) => void;
  setCroppedFile: (file: File) => void;
  setCategory: (category: Category) => void;
  setIsPaid: (isPaid: boolean) => void;
  setVotesTarget: (target: number) => void;
  setDemographicsGender: (gender: "any" | "male" | "female") => void;
  setDemographicsAge: (minAge: number, maxAge: number) => void;
  submitForm: () => Promise<void>;
}

export function useSubmissionForm(options?: UseSubmissionFormOptions): UseSubmissionFormReturn {
  const [formData, setFormData] = useState<FormData>(() => createInitialFormData(options));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setImageFile = (file: File | null) => {
    setFormData((prev) => ({ ...prev, imageFile: file, croppedFile: null }));
  };

  const setCroppedFile = (file: File) => {
    setFormData((prev) => ({ ...prev, croppedFile: file }));
  };

  const setCategory = (category: Category) => {
    setFormData((prev) => ({ ...prev, category }));
  };

  const setIsPaid = (isPaid: boolean) => {
    setFormData((prev) => ({
      ...prev,
      isPaid,
      votesTarget: isPaid && prev.votesTarget === 10 ? 25 : prev.votesTarget,
    }));
  };

  const setVotesTarget = (votesTarget: number) => {
    setFormData((prev) => ({ ...prev, votesTarget }));
  };

  const setDemographicsGender = (gender: "any" | "male" | "female") => {
    setFormData((prev) => ({
      ...prev,
      demographics: { ...prev.demographics!, gender },
    }));
  };

  const setDemographicsAge = (minAge: number, maxAge: number) => {
    setFormData((prev) => ({
      ...prev,
      demographics: { ...prev.demographics!, minAge, maxAge },
    }));
  };

  const submitForm = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      if (!formData.category) {
        throw new Error("Categoria é obrigatória");
      }

      const demographics =
        formData.isPaid && formData.demographics
          ? {
              gender: formData.demographics.gender,
              min_age: formData.demographics.minAge,
              max_age: formData.demographics.maxAge,
            }
          : undefined;

      const postOptions = {
        onError: (errors: Record<string, string | string[]>) => {
          const errorMessages = Object.values(errors).flat().join(", ");
          setError(errorMessages);
          setIsSubmitting(false);
        },
        onSuccess: () => {
          router.visit("/submissions");
        },
      };

      if (formData.photoId) {
        router.post(
          "/submissions",
          {
            submission: {
              photo_id: formData.photoId,
              category: formData.category,
              votes_target: formData.votesTarget,
              paid: formData.isPaid,
              demographics,
            },
          },
          postOptions
        );
      } else {
        const fileToUpload = formData.croppedFile ?? formData.imageFile;
        if (!fileToUpload) {
          throw new Error("Imagem é obrigatória");
        }

        router.post(
          "/submissions",
          {
            submission: {
              image: fileToUpload,
              category: formData.category,
              votes_target: formData.votesTarget,
              paid: formData.isPaid,
              demographics,
            },
          },
          postOptions
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ocorreu um erro");
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    isSubmitting,
    error,
    setError,
    setImageFile,
    setCroppedFile,
    setCategory,
    setIsPaid,
    setVotesTarget,
    setDemographicsGender,
    setDemographicsAge,
    submitForm,
  };
}
